import { useExpertUniversityStore } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import ky, { HTTPError, type BeforeErrorHook, type BeforeRequestHook, type KyInstance } from "ky";

import { userCache } from "@/shared-utils/auth";

import type { EvaluationResultsErrorResponse, TranscriptContent } from "./types";

import { env } from "../environment";

export class EvaluationApiClient {
    static readonly logger = getLogger({ module: "Expert University Evaluation Api" });
    static readonly taskId = useExpertUniversityStore.getState().sessionId;
    private api: KyInstance;

    constructor() {
        this.api = ky.create({
            prefix: env.expertUniversityEvaluationApiUrl,
            timeout: 30_000,
            hooks: {
                beforeRequest: [this.createAuthorizationBearerHook(() => userCache.ssoAccessToken)],
                beforeError: [this.createErrorLoggingHook()],
            },
        });
    }

    triggerEvaluationBySessionId = async (
        sessionId: string,
    ): Promise<TranscriptContent | EvaluationResultsErrorResponse> => {
        const taskId = sessionId ?? EvaluationApiClient.taskId;
        try {
            EvaluationApiClient.logger.info({ taskId }, "Triggering evaluation by session ID");
            trackFullStoryEvent("Evaluation Triggered", { taskId });
            const response = await this.api.post(`ai-roleplay-evaluations`, {
                json: {
                    session_id: sessionId,
                    source: "exwo",
                },
            });

            return response.json<TranscriptContent | EvaluationResultsErrorResponse>();
        } catch (error: any) {
            EvaluationApiClient.logger.warn(
                { error: error.message, taskId },
                "Error triggering evaluation by session ID",
            );
            return error as EvaluationResultsErrorResponse;
        }
    };

    private createAuthorizationBearerHook(tokenProvider: () => string | undefined): BeforeRequestHook {
        return ({ request: { headers } }) => {
            if (headers.has("Authorization")) return;
            const accessToken = tokenProvider();
            if (!accessToken) {
                throw new Error("Access token doesn’t exist");
            }
            headers.set("Authorization", `Bearer ${accessToken}`);
        };
    }

    private createErrorLoggingHook(): BeforeErrorHook {
        return ({ error }) => {
            if (!(error instanceof HTTPError)) {
                EvaluationApiClient.logger.error(
                    { error, sessionId: EvaluationApiClient.taskId },
                    "Non-HTTP error occurred during API request",
                );
                trackFullStoryEvent("API Non-HTTP Error", {
                    error: String(error),
                    sessionId: EvaluationApiClient.taskId,
                });
                return error;
            }
            const {
                request: { method, url },
                response: { status, statusText },
            } = error;
            EvaluationApiClient.logger.info(
                { method, url, statusCode: status, statusText, sessionId: EvaluationApiClient.taskId },
                "received an HTTP Error",
            );
            trackFullStoryEvent("API Error", {
                method,
                url,
                status,
                statusText,
                sessionId: EvaluationApiClient.taskId,
            });
            return error;
        };
    }
}
